import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Image as ImageIcon, Loader2, Save, Trash2 } from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";

import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from "../../components/ui/Dropzone";
import { Input } from "../../components/ui/Input";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useCloudflareUpload } from "../../hooks/useCloudflareUpload";

type StoreSettings = {
  id?: number;
  storeName: string;
  companyName: string;
  cui: string;
  registrulComertului: string;
  phone: string;
  email: string;
  address: string;
  logoUrl: string;
  description: string;
  websiteUrl: string;
  facebookUrl: string;
  instagramUrl: string;
  tiktokUrl: string;
  metadata: unknown;
} | null;

const defaultFormState: Omit<NonNullable<StoreSettings>, "id" | "metadata"> & {
  metadataText: string;
  shippingCostDefault?: string;
  freeShippingMinItems?: string | null;
  freeShippingMinTotal?: string | null;
  bundlePricingEnabled?: boolean;
  bundleMinItems?: string | null;
  bundlePricePerItem?: string | null;
  extraRambursPrice?: string;
} = {
  storeName: "",
  companyName: "",
  cui: "",
  registrulComertului: "",
  phone: "",
  email: "",
  address: "",
  logoUrl: "",
  description: "",
  websiteUrl: "",
  facebookUrl: "",
  instagramUrl: "",
  tiktokUrl: "",
  metadataText: "{}",
  shippingCostDefault: "",
  freeShippingMinItems: "",
  freeShippingMinTotal: "",
  bundlePricingEnabled: false,
  bundleMinItems: "",
  bundlePricePerItem: "",
  extraRambursPrice: "",
};

export default function StoreSettingsPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = React.useState(defaultFormState);

  // Logo uploader
  const logoUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  React.useEffect(() => {
    if (logoUpload.isSuccess && logoUpload.uploadedResults.length > 0) {
      const url = logoUpload.uploadedResults[0].publicUrl;
      setForm((f) => ({ ...f, logoUrl: url }));
    }
  }, [logoUpload.isSuccess, logoUpload.uploadedResults]);

  // Load existing settings
  const { data: settings, isLoading } = useQuery<StoreSettings>({
    queryKey: ["store-settings"],
    queryFn: async () => {
      const res = await fetchWithAuth("/api/admin/settings/store");
      if (!res.ok)
        throw new Error("Eroare la încărcarea setărilor magazinului");
      return res.json();
    },
  });

  // Hydrate form when settings arrive
  React.useEffect(() => {
    if (settings) {
      setForm({
        storeName: settings.storeName || "",
        companyName: settings.companyName || "",
        cui: settings.cui || "",
        registrulComertului: settings.registrulComertului || "",
        phone: settings.phone || "",
        email: settings.email || "",
        address: settings.address || "",
        logoUrl: settings.logoUrl || "",
        description: settings.description || "",
        websiteUrl: settings.websiteUrl || "",
        facebookUrl: settings.facebookUrl || "",
        instagramUrl: settings.instagramUrl || "",
        tiktokUrl: settings.tiktokUrl || "",
        metadataText: (() => {
          try {
            return settings.metadata
              ? JSON.stringify(settings.metadata, null, 2)
              : "{}";
          } catch {
            return "{}";
          }
        })(),
        shippingCostDefault:
          (settings as any).shippingCostDefault != null
            ? String((settings as any).shippingCostDefault)
            : "",
        freeShippingMinItems:
          (settings as any).freeShippingMinItems != null
            ? String((settings as any).freeShippingMinItems)
            : "",
        freeShippingMinTotal:
          (settings as any).freeShippingMinTotal != null
            ? String((settings as any).freeShippingMinTotal)
            : "",
        bundlePricingEnabled: !!(settings as any).bundlePricingEnabled,
        bundleMinItems:
          (settings as any).bundleMinItems != null
            ? String((settings as any).bundleMinItems)
            : "",
        bundlePricePerItem:
          (settings as any).bundlePricePerItem != null
            ? String((settings as any).bundlePricePerItem)
            : "",
        extraRambursPrice:
          (settings as any).extraRambursPrice != null
            ? String((settings as any).extraRambursPrice)
            : "",
      });
    } else {
      setForm(defaultFormState);
    }
  }, [settings]);

  const upsertMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await fetchWithAuth("/api/admin/settings/store", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Eroare la salvarea setărilor magazinului");
      return res.json();
    },
    onSuccess: () => {
      toast.success("Setări salvate cu succes");
      queryClient.invalidateQueries({ queryKey: ["store-settings"] });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare la salvare"),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetchWithAuth("/api/admin/settings/store", {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Eroare la ștergerea setărilor magazinului");
      return res.json();
    },
    onSuccess: () => {
      toast.success("Setări resetate");
      setForm(defaultFormState);
      queryClient.invalidateQueries({ queryKey: ["store-settings"] });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare la resetare"),
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    let parsedMetadata: unknown = {};
    try {
      parsedMetadata = form.metadataText?.trim()
        ? JSON.parse(form.metadataText)
        : {};
    } catch (err) {
      toast.error("Metadata trebuie să fie JSON valid");
      return;
    }
    upsertMutation.mutate({
      storeName: form.storeName.trim(),
      companyName: form.companyName.trim(),
      cui: form.cui.trim(),
      registrulComertului: form.registrulComertului.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      logoUrl: form.logoUrl.trim(),
      description: form.description,
      websiteUrl: form.websiteUrl.trim(),
      facebookUrl: form.facebookUrl.trim(),
      instagramUrl: form.instagramUrl.trim(),
      tiktokUrl: form.tiktokUrl.trim(),
      metadata: parsedMetadata,
      shippingCostDefault:
        form.shippingCostDefault !== "" && form.shippingCostDefault != null
          ? Number(form.shippingCostDefault)
          : undefined,
      freeShippingMinItems:
        form.freeShippingMinItems === ""
          ? null
          : form.freeShippingMinItems == null
          ? undefined
          : Number(form.freeShippingMinItems),
      freeShippingMinTotal:
        form.freeShippingMinTotal === ""
          ? null
          : form.freeShippingMinTotal == null
          ? undefined
          : Number(form.freeShippingMinTotal),
      bundlePricingEnabled: !!form.bundlePricingEnabled,
      bundleMinItems:
        form.bundleMinItems === ""
          ? null
          : form.bundleMinItems == null
          ? undefined
          : Number(form.bundleMinItems),
      bundlePricePerItem:
        form.bundlePricePerItem === ""
          ? null
          : form.bundlePricePerItem == null
          ? undefined
          : Number(form.bundlePricePerItem),
      extraRambursPrice:
        form.extraRambursPrice !== "" && form.extraRambursPrice != null
          ? Number(form.extraRambursPrice)
          : undefined,
    });
  };

  return (
    <div className="p-6 space-y-8 bg-background min-h-screen">
      <Card className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Setări Magazin</h1>
          <p className="text-sm text-muted-foreground">
            Administrează informațiile magazinului și integrarea
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex items-center gap-1 text-red-600 hover:text-red-700"
            disabled={deleteMutation.isPending || isLoading}
            onClick={() => {
              if (
                window.confirm(
                  "Sigur dorești să resetezi setările magazinului?"
                )
              ) {
                deleteMutation.mutate();
              }
            }}
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Resetare...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" /> Resetează
              </>
            )}
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Date generale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Nume magazin
                </label>
                <Input
                  value={form.storeName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, storeName: e.target.value }))
                  }
                  placeholder="Ex: Black Family Store"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Nume companie
                </label>
                <Input
                  value={form.companyName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, companyName: e.target.value }))
                  }
                  placeholder="Ex: SC Exemplu SRL"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  CUI
                </label>
                <Input
                  value={form.cui}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, cui: e.target.value }))
                  }
                  placeholder="RO12345678"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Registrul Comerțului
                </label>
                <Input
                  value={form.registrulComertului}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      registrulComertului: e.target.value,
                    }))
                  }
                  placeholder="J00/0000/2024"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Telefon
                </label>
                <Input
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  placeholder="07xx xxx xxx"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Email
                </label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder="contact@exemplu.ro"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Adresă
                </label>
                <Input
                  value={form.address}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, address: e.target.value }))
                  }
                  placeholder="Str. Exemplu nr. 1, București"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> Logo magazin
              </label>
              {form.logoUrl ? (
                <div className="flex items-center gap-4">
                  <img
                    src={form.logoUrl}
                    alt="Logo"
                    className="h-16 w-16 object-contain border rounded"
                  />
                  <Button
                    variant="outline"
                    onClick={() => setForm((f) => ({ ...f, logoUrl: "" }))}
                  >
                    Schimbă
                  </Button>
                </div>
              ) : (
                <>
                  <Dropzone {...logoUpload}>
                    <DropzoneEmptyState />
                    <DropzoneContent />
                  </Dropzone>
                  <div className="mt-3">
                    <p className="text-sm text-muted-foreground mb-2">
                      Sau introdu URL-ul logo-ului:
                    </p>
                    <Input
                      value={form.logoUrl}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, logoUrl: e.target.value }))
                      }
                      placeholder="https://exemplu.ro/logo.png"
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Descriere
              </label>
              <textarea
                className="min-h-[100px] max-h-[300px] resize-y w-full bg-background border-2 border-gray-300 rounded-md p-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Descriere scurtă a magazinului"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Website
                </label>
                <Input
                  value={form.websiteUrl}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, websiteUrl: e.target.value }))
                  }
                  placeholder="https://exemplu.ro"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Facebook
                </label>
                <Input
                  value={form.facebookUrl}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, facebookUrl: e.target.value }))
                  }
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Instagram
                </label>
                <Input
                  value={form.instagramUrl}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, instagramUrl: e.target.value }))
                  }
                  placeholder="https://instagram.com/..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  TikTok
                </label>
                <Input
                  value={form.tiktokUrl}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, tiktokUrl: e.target.value }))
                  }
                  placeholder="https://tiktok.com/@..."
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Metadata (JSON)
              </label>
              <textarea
                className="min-h-[140px] max-h-[400px] resize-y w-full bg-background border-2 border-gray-300 rounded-md p-3 font-mono text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                value={form.metadataText}
                onChange={(e) =>
                  setForm((f) => ({ ...f, metadataText: e.target.value }))
                }
                placeholder={`{\n  "exemplu": true\n}`}
              />
            </div>

            {/* Promoții & Livrare */}
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-foreground">
                Promoții & Livrare
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Cost livrare implicit (RON)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.shippingCostDefault ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        shippingCostDefault: e.target.value,
                      }))
                    }
                    placeholder="Ex: 19.9"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Livrare gratuită de la N articole
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.freeShippingMinItems ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        freeShippingMinItems: e.target.value,
                      }))
                    }
                    placeholder="Ex: 3"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Livrare gratuită de la total (RON)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.freeShippingMinTotal ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        freeShippingMinTotal: e.target.value,
                      }))
                    }
                    placeholder="Ex: 150"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="text-sm font-medium text-muted-foreground">
                    Taxă ramburs suplimentară (RON)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.extraRambursPrice ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        extraRambursPrice: e.target.value,
                      }))
                    }
                    placeholder="Ex: 5"
                  />
                </div>
              </div>

              <div className="p-3 rounded-md border bg-muted/20 space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    id="bundlePricingEnabled"
                    type="checkbox"
                    checked={!!form.bundlePricingEnabled}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        bundlePricingEnabled: e.target.checked,
                      }))
                    }
                    className="w-4 h-4"
                  />
                  <label
                    htmlFor="bundlePricingEnabled"
                    className="text-sm text-foreground"
                  >
                    Activează prețuri de pachet (bundle)
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Min. articole pentru bundle
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={form.bundleMinItems ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          bundleMinItems: e.target.value,
                        }))
                      }
                      placeholder="Ex: 2"
                      disabled={!form.bundlePricingEnabled}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Preț per articol în bundle (RON)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.bundlePricePerItem ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          bundlePricePerItem: e.target.value,
                        }))
                      }
                      placeholder="Ex: 29.9"
                      disabled={!form.bundlePricingEnabled}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="submit"
                disabled={upsertMutation.isPending || isLoading}
                className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
              >
                {upsertMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Salvare...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
